// Hero — single conversion focus: Save ₹10,000+ for ₹1,249.
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Star, Zap, MapPin } from "lucide-react";
import CPCountdown from "./CPCountdown";

export default function CPHero({ onBuyClick, slotsLeft, variant = "a" }) {
  const isB = variant === "b";
  const scrollToPricing = (e) => {
    e?.preventDefault();
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section
      data-testid="cp-hero"
      data-variant={variant}
      className="relative bg-[#0A0A0A] text-white px-5 lg:px-8 pt-10 pb-16 sm:pt-16 sm:pb-24 overflow-hidden"
    >
      {/* radial glow */}
      <div className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(ellipse at 70% 0%, rgba(212,175,55,0.25) 0%, transparent 55%)" }} />

      <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/12 border border-[#D4AF37]/40 rounded-sm px-3 py-1.5 mb-5">
            <Zap className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#D4AF37]">
              Limited — Only {slotsLeft} slots left
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
            {isB ? (
              <>
                <span className="text-[#D4AF37]">₹4 a day.</span>
                <br />Premium car care.
                <br />
                <span className="text-white/70 text-3xl sm:text-4xl lg:text-5xl font-semibold">
                  Just ₹1,249/year.
                </span>
              </>
            ) : (
              <>
                Save <span className="text-[#D4AF37]">₹10,000+</span>
                <br />on car care
                <br />
                <span className="text-white/70 text-3xl sm:text-4xl lg:text-5xl font-semibold">
                  for just ₹1,249/year.
                </span>
              </>
            )}
          </h1>

          <p className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
            {isB ? (
              <>
                <strong className="text-white">AutoHQ Care+</strong> covers 20+ services —
                3 free washes, 2 free AC check-ups, alignment, denting & labour off — worth
                <strong className="text-white"> ₹10,000+</strong>. 365-day validity. Up to 2 cars.
              </>
            ) : (
              <>
                <strong className="text-white">AutoHQ Care+</strong> gets you 20+ redemptions —
                washes, AC service, alignment, denting & labour discounts — across
                <strong className="text-white"> 365 days</strong>. Up to 2 cars. 100% genuine parts.
              </>
            )}
          </p>

          {/* Pricing row */}
          <div className="mt-8 flex items-end gap-5 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold mb-1.5">Plans start at</p>
              <div className="flex items-baseline gap-3">
                <span className="text-5xl sm:text-6xl font-extrabold text-[#D4AF37]">₹549</span>
                <span className="text-base sm:text-lg text-white/60 font-medium">/ 3 months</span>
              </div>
              <p className="text-xs text-white/60 mt-1.5">
                6 months <span className="font-bold text-white">₹749</span> · 12 months <span className="font-bold text-white">₹1,249</span> (save 87%)
              </p>
            </div>
          </div>

          {/* CTA row */}
          <div className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md">
            <Button
              data-testid="cp-hero-buy-cta"
              onClick={onBuyClick}
              className="bg-[#D4AF37] hover:bg-[#E5C254] text-[#0A0A0A] font-extrabold text-base px-7 py-7 rounded-sm flex-1 shadow-[0_8px_30px_rgba(212,175,55,0.35)]"
            >
              {isB ? "Start saving →" : "Get Care+ now →"}
            </Button>
            <a
              data-testid="cp-hero-see-plans"
              href="#pricing"
              onClick={scrollToPricing}
              className="px-5 py-3 sm:py-7 border border-white/30 text-white/90 hover:text-white hover:bg-white/5 text-sm font-bold uppercase tracking-wider rounded-sm flex items-center justify-center transition-colors"
            >
              See all plans
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/60">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> Razorpay secured</span>
            <span className="flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5 text-[#D4AF37] fill-[#D4AF37]" /> 4.8 / 5 (1,200+)
            </span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#D4AF37]" /> Ranigunj, Hyderabad</span>
          </div>
        </motion.div>

        {/* Right column — countdown + offer card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 rounded-sm p-6 sm:p-8 backdrop-blur"
        >
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Offer ends in</p>
          <div className="mt-3 mb-6">
            <CPCountdown />
          </div>

          <div className="border-t border-white/10 pt-5 space-y-2.5 text-sm">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/50 mb-2">Inside 12-month plan</p>
            {[
              "✓ 3 Free Car Washes (worth ₹1,800)",
              "✓ 2 Free AC Check-ups (worth ₹1,400)",
              "✓ Free Wheel Alignment (worth ₹600)",
              "✓ 100% Off Labour on General Service",
              "✓ ₹500 Off Denting/Painting × 2",
              "✓ 20+ redemptions in total",
            ].map((b, i) => (
              <p key={i} className="text-white/80 leading-snug">{b}</p>
            ))}
          </div>

          <Button
            data-testid="cp-hero-buy-cta-side"
            onClick={onBuyClick}
            className="mt-6 w-full bg-white hover:bg-white/90 text-[#0A0A0A] font-extrabold text-sm py-5 rounded-sm uppercase tracking-wider"
          >
            Activate 12-Month Care+
          </Button>
          <a
            href="#pricing"
            onClick={scrollToPricing}
            data-testid="cp-hero-side-see-plans"
            className="block text-center text-[11px] text-white/60 hover:text-[#D4AF37] mt-3 uppercase tracking-wider"
          >
            Or compare 3m · 6m · 12m plans →
          </a>
        </motion.div>
      </div>
    </section>
  );
}
